"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Trie = void 0;
const util_1 = require("@ethereumjs/util");
const keccak_1 = require("ethereum-cryptography/keccak");
const db_1 = require("../db");
const range_1 = require("../proof/range");
const types_1 = require("../types");
const lock_1 = require("../util/lock");
const nibbles_1 = require("../util/nibbles");
const readStream_1 = require("../util/readStream");
const walkController_1 = require("../util/walkController");
const node_1 = require("./node");
/**
 * The basic trie interface, use with `import { Trie } from '@ethereumjs/trie'`.
 * In Ethereum applications stick with the {@link SecureTrie} overlay.
 * The API for the base and the secure interface are about the same.
 */
class Trie {
    /**
     * Create a new trie
     * @param opts Options for instantiating the trie
     */
    constructor(opts) {
        this._opts = {
            useKeyHashing: false,
            useKeyHashingFunction: keccak_1.keccak256,
            useRootPersistence: false,
            useNodePruning: false,
        };
        this._lock = new lock_1.Lock();
        if (opts !== undefined) {
            this._opts = { ...this._opts, ...opts };
        }
        this.database(opts?.db ?? new db_1.MapDB());
        this.EMPTY_TRIE_ROOT = this.hash(util_1.RLP_EMPTY_STRING);
        this._hashLen = this.EMPTY_TRIE_ROOT.length;
        this._root = this.EMPTY_TRIE_ROOT;
        if (opts?.root) {
            this.root(opts.root);
        }
    }
    static async create(opts) {
        let key = types_1.ROOT_DB_KEY;
        if (opts?.useKeyHashing === true) {
            key = (opts?.useKeyHashingFunction ?? keccak_1.keccak256)(types_1.ROOT_DB_KEY);
        }
        key = Buffer.from(key);
        if (opts?.db !== undefined && opts?.useRootPersistence === true) {
            if (opts?.root === undefined) {
                opts.root = (await opts?.db.get(key)) ?? undefined;
            }
            else {
                await opts?.db.put(key, opts.root);
            }
        }
        return new Trie(opts);
    }
    database(db) {
        if (db !== undefined) {
            if (db instanceof db_1.CheckpointDB) {
                throw new Error('Cannot pass in an instance of CheckpointDB');
            }
            this._db = new db_1.CheckpointDB(db);
        }
        return this._db;
    }
    /**
     * Gets and/or Sets the current root of the `trie`
     */
    root(value) {
        if (value !== undefined) {
            if (value === null) {
                value = this.EMPTY_TRIE_ROOT;
            }
            if (value.length !== this._hashLen) {
                throw new Error(`Invalid root length. Roots are ${this._hashLen} bytes`);
            }
            this._root = value;
        }
        return this._root;
    }
    /**
     * Checks if a given root exists.
     */
    async checkRoot(root) {
        try {
            const value = await this.lookupNode(root);
            return value !== null;
        }
        catch (error) {
            if (error.message === 'Missing node in DB') {
                return false;
            }
            else {
                throw error;
            }
        }
    }
    /**
     * Gets a value given a `key`
     * @param key - the key to search for
     * @param throwIfMissing - if true, throws if any nodes are missing. Used for verifying proofs. (default: false)
     * @returns A Promise that resolves to `Buffer` if a value was found or `null` if no value was found.
     */
    async get(key, throwIfMissing = false) {
        const { node, remaining } = await this.findPath(this.appliedKey(key), throwIfMissing);
        let value = null;
        if (node && remaining.length === 0) {
            value = node.value();
        }
        return value;
    }
    /**
     * Stores a given `value` at the given `key` or do a delete if `value` is empty
     * (delete operations are only executed on DB with `deleteFromDB` set to `true`)
     * @param key
     * @param value
     * @returns A Promise that resolves once value is stored.
     */
    async put(key, value) {
        if (this._opts.useRootPersistence && key.equals(types_1.ROOT_DB_KEY)) {
            throw new Error(`Attempted to set '${types_1.ROOT_DB_KEY.toString()}' key but it is not allowed.`);
        }
        // If value is empty, delete
        if (value === null || value.length === 0) {
            return await this.del(key);
        }
        await this._lock.acquire();
        const appliedKey = this.appliedKey(key);
        if (this.root().equals(this.EMPTY_TRIE_ROOT)) {
            // If no root, initialize this trie
            await this._createInitialNode(appliedKey, value);
        }
        else {
            // First try to find the given key or its nearest node
            const { remaining, stack } = await this.findPath(appliedKey);
            let ops = [];
            if (this._opts.useNodePruning) {
                const val = await this.get(key);
                // Only delete keys if it either does not exist, or if it gets updated
                // (The update will update the hash of the node, thus we can delete the original leaf node)
                if (val === null || !val.equals(value)) {
                    // All items of the stack are going to change.
                    // (This is the path from the root node to wherever it needs to insert nodes)
                    // The items change, because the leaf value is updated, thus all keyhashes in the
                    // stack should be updated as well, so that it points to the right key/value pairs of the path
                    const deleteHashes = stack.map((e) => this.hash(e.serialize()));
                    ops = deleteHashes.map((e) => {
                        return {
                            type: 'del',
                            key: e,
                        };
                    });
                }
            }
            // then update
            await this._updateNode(appliedKey, value, remaining, stack);
            if (this._opts.useNodePruning) {
                // Only after updating the node we can delete the keyhashes
                await this._db.batch(ops);
            }
        }
        await this.persistRoot();
        this._lock.release();
    }
    /**
     * Deletes a value given a `key` from the trie
     * (delete operations are only executed on DB with `deleteFromDB` set to `true`)
     * @param key
     * @returns A Promise that resolves once value is deleted.
     */
    async del(key) {
        await this._lock.acquire();
        const appliedKey = this.appliedKey(key);
        const { node, stack } = await this.findPath(appliedKey);
        let ops = [];
        // Only delete if the `key` currently has any value
        if (this._opts.useNodePruning && node !== null) {
            const deleteHashes = stack.map((e) => this.hash(e.serialize()));
            // Just as with `put`, the stack items all will have their keyhashes updated
            // So after deleting the node, one can safely delete these from the DB
            ops = deleteHashes.map((e) => {
                return {
                    type: 'del',
                    key: e,
                };
            });
        }
        if (node) {
            await this._deleteNode(appliedKey, stack);
        }
        if (this._opts.useNodePruning) {
            // Only after deleting the node it is possible to delete the keyhashes
            await this._db.batch(ops);
        }
        await this.persistRoot();
        this._lock.release();
    }
    /**
     * Tries to find a path to the node for the given key.
     * It returns a `stack` of nodes to the closest node.
     * @param key - the search key
     * @param throwIfMissing - if true, throws if any nodes are missing. Used for verifying proofs. (default: false)
     */
    async findPath(key, throwIfMissing = false) {
        // eslint-disable-next-line no-async-promise-executor
        return new Promise(async (resolve, reject) => {
            const stack = [];
            const targetKey = (0, nibbles_1.bufferToNibbles)(key);
            const onFound = async (_, node, keyProgress, walkController) => {
                if (node === null) {
                    return reject(new Error('Path not found'));
                }
                const keyRemainder = targetKey.slice((0, nibbles_1.matchingNibbleLength)(keyProgress, targetKey));
                stack.push(node);
                if (node instanceof node_1.BranchNode) {
                    if (keyRemainder.length === 0) {
                        // we exhausted the key without finding a node
                        resolve({ node, remaining: [], stack });
                    }
                    else {
                        const branchIndex = keyRemainder[0];
                        const branchNode = node.getBranch(branchIndex);
                        if (!branchNode) {
                            // there are no more nodes to find and we didn't find the key
                            resolve({ node: null, remaining: keyRemainder, stack });
                        }
                        else {
                            // node found, continuing search
                            // this can be optimized as this calls getBranch again.
                            walkController.onlyBranchIndex(node, keyProgress, branchIndex);
                        }
                    }
                }
                else if (node instanceof node_1.LeafNode) {
                    if ((0, nibbles_1.doKeysMatch)(keyRemainder, node.key())) {
                        // keys match, return node with empty key
                        resolve({ node, remaining: [], stack });
                    }
                    else {
                        // reached leaf but keys dont match
                        resolve({ node: null, remaining: keyRemainder, stack });
                    }
                }
                else if (node instanceof node_1.ExtensionNode) {
                    const matchingLen = (0, nibbles_1.matchingNibbleLength)(keyRemainder, node.key());
                    if (matchingLen !== node.key().length) {
                        // keys don't match, fail
                        resolve({ node: null, remaining: keyRemainder, stack });
                    }
                    else {
                        // keys match, continue search
                        walkController.allChildren(node, keyProgress);
                    }
                }
            };
            // walk trie and process nodes
            try {
                await this.walkTrie(this.root(), onFound);
            }
            catch (error) {
                if (error.message === 'Missing node in DB' && !throwIfMissing) {
                    // pass
                }
                else {
                    reject(error);
                }
            }
            // Resolve if walkTrie finishes without finding any nodes
            resolve({ node: null, remaining: [], stack });
        });
    }
    /**
     * Walks a trie until finished.
     * @param root
     * @param onFound - callback to call when a node is found. This schedules new tasks. If no tasks are available, the Promise resolves.
     * @returns Resolves when finished walking trie.
     */
    async walkTrie(root, onFound) {
        await walkController_1.WalkController.newWalk(onFound, this, root);
    }
    /**
     * Creates the initial node from an empty tree.
     * @private
     */
    async _createInitialNode(key, value) {
        const newNode = new node_1.LeafNode((0, nibbles_1.bufferToNibbles)(key), value);
        const encoded = newNode.serialize();
        this.root(this.hash(encoded));
        await this._db.put(this.root(), encoded);
        await this.persistRoot();
    }
    /**
     * Retrieves a node from db by hash.
     */
    async lookupNode(node) {
        if ((0, node_1.isRawNode)(node)) {
            return (0, node_1.decodeRawNode)(node);
        }
        let value = null;
        let foundNode = null;
        value = await this._db.get(node);
        if (value) {
            foundNode = (0, node_1.decodeNode)(value);
        }
        else {
            // Dev note: this error message text is used for error checking in `checkRoot`, `verifyProof`, and `findPath`
            throw new Error('Missing node in DB');
        }
        return foundNode;
    }
    /**
     * Updates a node.
     * @private
     * @param key
     * @param value
     * @param keyRemainder
     * @param stack
     */
    async _updateNode(k, value, keyRemainder, stack) {
        const toSave = [];
        const lastNode = stack.pop();
        if (!lastNode) {
            throw new Error('Stack underflow');
        }
        // add the new nodes
        const key = (0, nibbles_1.bufferToNibbles)(k);
        // Check if the last node is a leaf and the key matches to this
        let matchLeaf = false;
        if (lastNode instanceof node_1.LeafNode) {
            let l = 0;
            for (let i = 0; i < stack.length; i++) {
                const n = stack[i];
                if (n instanceof node_1.BranchNode) {
                    l++;
                }
                else {
                    l += n.key().length;
                }
            }
            if ((0, nibbles_1.matchingNibbleLength)(lastNode.key(), key.slice(l)) === lastNode.key().length &&
                keyRemainder.length === 0) {
                matchLeaf = true;
            }
        }
        if (matchLeaf) {
            // just updating a found value
            lastNode.value(value);
            stack.push(lastNode);
        }
        else if (lastNode instanceof node_1.BranchNode) {
            stack.push(lastNode);
            if (keyRemainder.length !== 0) {
                // add an extension to a branch node
                keyRemainder.shift();
                // create a new leaf
                const newLeaf = new node_1.LeafNode(keyRemainder, value);
                stack.push(newLeaf);
            }
            else {
                lastNode.value(value);
            }
        }
        else {
            // create a branch node
            const lastKey = lastNode.key();
            const matchingLength = (0, nibbles_1.matchingNibbleLength)(lastKey, keyRemainder);
            const newBranchNode = new node_1.BranchNode();
            // create a new extension node
            if (matchingLength !== 0) {
                const newKey = lastNode.key().slice(0, matchingLength);
                const newExtNode = new node_1.ExtensionNode(newKey, value);
                stack.push(newExtNode);
                lastKey.splice(0, matchingLength);
                keyRemainder.splice(0, matchingLength);
            }
            stack.push(newBranchNode);
            if (lastKey.length !== 0) {
                const branchKey = lastKey.shift();
                if (lastKey.length !== 0 || lastNode instanceof node_1.LeafNode) {
                    // shrinking extension or leaf
                    lastNode.key(lastKey);
                    const formattedNode = this._formatNode(lastNode, false, toSave);
                    newBranchNode.setBranch(branchKey, formattedNode);
                }
                else {
                    // remove extension or attaching
                    this._formatNode(lastNode, false, toSave, true);
                    newBranchNode.setBranch(branchKey, lastNode.value());
                }
            }
            else {
                newBranchNode.value(lastNode.value());
            }
            if (keyRemainder.length !== 0) {
                keyRemainder.shift();
                // add a leaf node to the new branch node
                const newLeafNode = new node_1.LeafNode(keyRemainder, value);
                stack.push(newLeafNode);
            }
            else {
                newBranchNode.value(value);
            }
        }
        await this._saveStack(key, stack, toSave);
    }
    /**
     * Deletes a node from the trie.
     * @private
     */
    async _deleteNode(k, stack) {
        const processBranchNode = (key, branchKey, branchNode, parentNode, stack) => {
            // branchNode is the node ON the branch node not THE branch node
            if (parentNode === null || parentNode === undefined || parentNode instanceof node_1.BranchNode) {
                // branch->?
                if (parentNode !== null && parentNode !== undefined) {
                    stack.push(parentNode);
                }
                if (branchNode instanceof node_1.BranchNode) {
                    // create an extension node
                    // branch->extension->branch
                    // @ts-ignore
                    const extensionNode = new node_1.ExtensionNode([branchKey], null);
                    stack.push(extensionNode);
                    key.push(branchKey);
                }
                else {
                    const branchNodeKey = branchNode.key();
                    // branch key is an extension or a leaf
                    // branch->(leaf or extension)
                    branchNodeKey.unshift(branchKey);
                    branchNode.key(branchNodeKey.slice(0));
                    key = key.concat(branchNodeKey);
                }
                stack.push(branchNode);
            }
            else {
                // parent is an extension
                let parentKey = parentNode.key();
                if (branchNode instanceof node_1.BranchNode) {
                    // ext->branch
                    parentKey.push(branchKey);
                    key.push(branchKey);
                    parentNode.key(parentKey);
                    stack.push(parentNode);
                }
                else {
                    const branchNodeKey = branchNode.key();
                    // branch node is an leaf or extension and parent node is an extension
                    // add two keys together
                    // don't push the parent node
                    branchNodeKey.unshift(branchKey);
                    key = key.concat(branchNodeKey);
                    parentKey = parentKey.concat(branchNodeKey);
                    branchNode.key(parentKey);
                }
                stack.push(branchNode);
            }
            return key;
        };
        let lastNode = stack.pop();
        if (lastNode === undefined)
            throw new Error('missing last node');
        let parentNode = stack.pop();
        const opStack = [];
        let key = (0, nibbles_1.bufferToNibbles)(k);
        if (!parentNode) {
            // the root here has to be a leaf.
            this.root(this.EMPTY_TRIE_ROOT);
            return;
        }
        if (lastNode instanceof node_1.BranchNode) {
            lastNode.value(null);
        }
        else {
            // the lastNode has to be a leaf if it's not a branch.
            // And a leaf's parent, if it has one, must be a branch.
            if (!(parentNode instanceof node_1.BranchNode)) {
                throw new Error('Expected branch node');
            }
            const lastNodeKey = lastNode.key();
            key.splice(key.length - lastNodeKey.length);
            // delete the value
            this._formatNode(lastNode, false, opStack, true);
            parentNode.setBranch(key.pop(), null);
            lastNode = parentNode;
            parentNode = stack.pop();
        }
        // nodes on the branch
        // count the number of nodes on the branch
        const branchNodes = lastNode.getChildren();
        // if there is only one branch node left, collapse the branch node
        if (branchNodes.length === 1) {
            // add the one remaing branch node to node above it
            const branchNode = branchNodes[0][1];
            const branchNodeKey = branchNodes[0][0];
            // Special case where one needs to delete an extra node:
            // In this case, after updating the branch, the branch node has just one branch left
            // However, this violates the trie spec; this should be converted in either an ExtensionNode
            // Or a LeafNode
            // Since this branch is deleted, one can thus also delete this branch from the DB
            // So add this to the `opStack` and mark the keyhash to be deleted
            if (this._opts.useNodePruning) {
                opStack.push({
                    type: 'del',
                    key: branchNode,
                });
            }
            // look up node
            const foundNode = await this.lookupNode(branchNode);
            if (foundNode) {
                key = processBranchNode(key, branchNodeKey, foundNode, parentNode, stack);
                await this._saveStack(key, stack, opStack);
            }
        }
        else {
            // simple removing a leaf and recaluclation the stack
            if (parentNode) {
                stack.push(parentNode);
            }
            stack.push(lastNode);
            await this._saveStack(key, stack, opStack);
        }
    }
    /**
     * Saves a stack of nodes to the database.
     * @private
     * @param key - the key. Should follow the stack
     * @param stack - a stack of nodes to the value given by the key
     * @param opStack - a stack of levelup operations to commit at the end of this funciton
     */
    async _saveStack(key, stack, opStack) {
        let lastRoot;
        // update nodes
        while (stack.length) {
            const node = stack.pop();
            if (node instanceof node_1.LeafNode) {
                key.splice(key.length - node.key().length);
            }
            else if (node instanceof node_1.ExtensionNode) {
                key.splice(key.length - node.key().length);
                if (lastRoot) {
                    node.value(lastRoot);
                }
            }
            else if (node instanceof node_1.BranchNode) {
                if (lastRoot) {
                    const branchKey = key.pop();
                    node.setBranch(branchKey, lastRoot);
                }
            }
            lastRoot = this._formatNode(node, stack.length === 0, opStack);
        }
        if (lastRoot) {
            this.root(lastRoot);
        }
        await this._db.batch(opStack);
        await this.persistRoot();
    }
    /**
     * Formats node to be saved by `levelup.batch`.
     * @private
     * @param node - the node to format.
     * @param topLevel - if the node is at the top level.
     * @param opStack - the opStack to push the node's data.
     * @param remove - whether to remove the node
     * @returns The node's hash used as the key or the rawNode.
     */
    _formatNode(node, topLevel, opStack, remove = false) {
        const encoded = node.serialize();
        if (encoded.length >= 32 || topLevel) {
            const hashRoot = Buffer.from(this.hash(encoded));
            if (remove) {
                if (this._opts.useNodePruning) {
                    opStack.push({
                        type: 'del',
                        key: hashRoot,
                    });
                }
            }
            else {
                opStack.push({
                    type: 'put',
                    key: hashRoot,
                    value: encoded,
                });
            }
            return hashRoot;
        }
        return node.raw();
    }
    /**
     * The given hash of operations (key additions or deletions) are executed on the trie
     * (delete operations are only executed on DB with `deleteFromDB` set to `true`)
     * @example
     * const ops = [
     *    { type: 'del', key: Buffer.from('father') }
     *  , { type: 'put', key: Buffer.from('name'), value: Buffer.from('Yuri Irsenovich Kim') }
     *  , { type: 'put', key: Buffer.from('dob'), value: Buffer.from('16 February 1941') }
     *  , { type: 'put', key: Buffer.from('spouse'), value: Buffer.from('Kim Young-sook') }
     *  , { type: 'put', key: Buffer.from('occupation'), value: Buffer.from('Clown') }
     * ]
     * await trie.batch(ops)
     * @param ops
     */
    async batch(ops) {
        for (const op of ops) {
            if (op.type === 'put') {
                if (op.value === null || op.value === undefined) {
                    throw new Error('Invalid batch db operation');
                }
                await this.put(op.key, op.value);
            }
            else if (op.type === 'del') {
                await this.del(op.key);
            }
        }
        await this.persistRoot();
    }
    /**
     * Saves the nodes from a proof into the trie.
     * @param proof
     */
    async fromProof(proof) {
        const opStack = proof.map((nodeValue) => {
            return {
                type: 'put',
                key: Buffer.from(this.hash(nodeValue)),
                value: nodeValue,
            };
        });
        if (this.root() === this.EMPTY_TRIE_ROOT && opStack[0] !== undefined && opStack[0] !== null) {
            this.root(opStack[0].key);
        }
        await this._db.batch(opStack);
        await this.persistRoot();
        return;
    }
    /**
     * Creates a proof from a trie and key that can be verified using {@link Trie.verifyProof}.
     * @param key
     */
    async createProof(key) {
        const { stack } = await this.findPath(this.appliedKey(key));
        const p = stack.map((stackElem) => {
            return stackElem.serialize();
        });
        return p;
    }
    /**
     * Verifies a proof.
     * @param rootHash
     * @param key
     * @param proof
     * @throws If proof is found to be invalid.
     * @returns The value from the key, or null if valid proof of non-existence.
     */
    async verifyProof(rootHash, key, proof) {
        const proofTrie = new Trie({
            root: rootHash,
            useKeyHashingFunction: this._opts.useKeyHashingFunction,
        });
        try {
            await proofTrie.fromProof(proof);
        }
        catch (e) {
            throw new Error('Invalid proof nodes given');
        }
        try {
            const value = await proofTrie.get(this.appliedKey(key), true);
            return value;
        }
        catch (err) {
            if (err.message === 'Missing node in DB') {
                throw new Error('Invalid proof provided');
            }
            else {
                throw err;
            }
        }
    }
    /**
     * {@link verifyRangeProof}
     */
    verifyRangeProof(rootHash, firstKey, lastKey, keys, values, proof) {
        return (0, range_1.verifyRangeProof)(rootHash, firstKey && (0, nibbles_1.bufferToNibbles)(this.appliedKey(firstKey)), lastKey && (0, nibbles_1.bufferToNibbles)(this.appliedKey(lastKey)), keys.map((k) => this.appliedKey(k)).map(nibbles_1.bufferToNibbles), values, proof, this._opts.useKeyHashingFunction);
    }
    // This method verifies if all keys in the trie (except the root) are reachable
    // If one of the key is not reachable, then that key could be deleted from the DB
    // (i.e. the Trie is not correctly pruned)
    // If this method returns `true`, the Trie is correctly pruned and all keys are reachable
    async verifyPrunedIntegrity() {
        const root = this.root().toString('hex');
        for (const dbkey of this._db.db._database.keys()) {
            if (dbkey === root) {
                // The root key can never be found from the trie, otherwise this would
                // convert the tree from a directed acyclic graph to a directed cycling graph
                continue;
            }
            // Track if key is found
            let found = false;
            try {
                await this.walkTrie(this.root(), async function (nodeRef, node, key, controller) {
                    if (found) {
                        // Abort all other children checks
                        return;
                    }
                    if (node instanceof node_1.BranchNode) {
                        for (const item of node._branches) {
                            // If one of the branches matches the key, then it is found
                            if (item && item.toString('hex') === dbkey) {
                                found = true;
                                return;
                            }
                        }
                        // Check all children of the branch
                        controller.allChildren(node, key);
                    }
                    if (node instanceof node_1.ExtensionNode) {
                        // If the value of the ExtensionNode points to the dbkey, then it is found
                        if (node.value().toString('hex') === dbkey) {
                            found = true;
                            return;
                        }
                        controller.allChildren(node, key);
                    }
                });
            }
            catch {
                return false;
            }
            if (!found) {
                return false;
            }
        }
        return true;
    }
    /**
     * The `data` event is given an `Object` that has two properties; the `key` and the `value`. Both should be Buffers.
     * @return Returns a [stream](https://nodejs.org/dist/latest-v12.x/docs/api/stream.html#stream_class_stream_readable) of the contents of the `trie`
     */
    createReadStream() {
        return new readStream_1.TrieReadStream(this);
    }
    /**
     * Returns a copy of the underlying trie.
     * @param includeCheckpoints - If true and during a checkpoint, the copy will contain the checkpointing metadata and will use the same scratch as underlying db.
     */
    copy(includeCheckpoints = true) {
        const trie = new Trie({
            ...this._opts,
            db: this._db.db.copy(),
            root: this.root(),
        });
        if (includeCheckpoints && this.hasCheckpoints()) {
            trie._db.setCheckpoints(this._db.checkpoints);
        }
        return trie;
    }
    /**
     * Persists the root hash in the underlying database
     */
    async persistRoot() {
        if (this._opts.useRootPersistence) {
            await this._db.put(this.appliedKey(types_1.ROOT_DB_KEY), this.root());
        }
    }
    /**
     * Finds all nodes that are stored directly in the db
     * (some nodes are stored raw inside other nodes)
     * called by {@link ScratchReadStream}
     * @private
     */
    async _findDbNodes(onFound) {
        const outerOnFound = async (nodeRef, node, key, walkController) => {
            if ((0, node_1.isRawNode)(nodeRef)) {
                if (node !== null) {
                    walkController.allChildren(node, key);
                }
            }
            else {
                onFound(nodeRef, node, key, walkController);
            }
        };
        await this.walkTrie(this.root(), outerOnFound);
    }
    /**
     * Returns the key practically applied for trie construction
     * depending on the `useKeyHashing` option being set or not.
     * @param key
     */
    appliedKey(key) {
        if (this._opts.useKeyHashing) {
            return this.hash(key);
        }
        return key;
    }
    hash(msg) {
        return Buffer.from(this._opts.useKeyHashingFunction(msg));
    }
    /**
     * Is the trie during a checkpoint phase?
     */
    hasCheckpoints() {
        return this._db.hasCheckpoints();
    }
    /**
     * Creates a checkpoint that can later be reverted to or committed.
     * After this is called, all changes can be reverted until `commit` is called.
     */
    checkpoint() {
        this._db.checkpoint(this.root());
    }
    /**
     * Commits a checkpoint to disk, if current checkpoint is not nested.
     * If nested, only sets the parent checkpoint as current checkpoint.
     * @throws If not during a checkpoint phase
     */
    async commit() {
        if (!this.hasCheckpoints()) {
            throw new Error('trying to commit when not checkpointed');
        }
        await this._lock.acquire();
        await this._db.commit();
        await this.persistRoot();
        this._lock.release();
    }
    /**
     * Reverts the trie to the state it was at when `checkpoint` was first called.
     * If during a nested checkpoint, sets root to most recent checkpoint, and sets
     * parent checkpoint as current.
     */
    async revert() {
        if (!this.hasCheckpoints()) {
            throw new Error('trying to revert when not checkpointed');
        }
        await this._lock.acquire();
        this.root(await this._db.revert());
        await this.persistRoot();
        this._lock.release();
    }
    /**
     * Flushes all checkpoints, restoring the initial checkpoint state.
     */
    flushCheckpoints() {
        this._db.checkpoints = [];
    }
}
exports.Trie = Trie;
//# sourceMappingURL=trie.js.map